import type { MetricChange, MetricResult, OperationsMetricId } from '@/api/snapshot'

/**
 * How the Overview writes its numbers, in the reader's language: counts with
 * their thousands separator, waits in whole minutes, changes with their sign.
 * Numbers never go into a translated string as text; they are formatted here
 * and interpolated (i18n-strings skill).
 */

/** The strings each metric's name and definition live under (`overview.metrics.<key>`). */
export const METRIC_KEYS: Record<OperationsMetricId, string> = {
  'METRIC-OP-APPOINTMENTS': 'appointments',
  'METRIC-OP-VISITS-COMPLETED': 'visitsCompleted',
  'METRIC-OP-PATIENTS-SEEN': 'patientsSeen',
  'METRIC-OP-WAITING-TIME': 'waitingTime',
  'METRIC-OP-ACTIVE-MEMBERS': 'activeMembers',
}

/** A count as a count; a duration in whole minutes (the caller adds the unit's word). */
export const formatNumber = (value: number, unit: MetricResult['unit'], locale: string): string =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(unit === 'MINUTES' ? Math.round(value) : value)

/**
 * The change as a reader expects it: a percentage for counts, minutes for a
 * duration (a 14-minute rise says more than "+58%"). Always signed.
 */
export const formatChange = (change: MetricChange, unit: MetricResult['unit'], locale: string): string => {
  if (unit === 'MINUTES' || change.relative === null) {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0, signDisplay: 'exceptZero' }).format(
      Math.round(change.absolute),
    )
  }
  return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0, signDisplay: 'exceptZero' }).format(
    change.relative,
  )
}

/** A calendar day, `YYYY-MM-DD`, as a Date at its UTC midnight: formatted in UTC, it never moves a day. */
const dayAsDate = (day: string): Date => new Date(`${day}T00:00:00.000Z`)

/** "24 – 30 Sep", or "30 Sep" for one day, in the reader's language; with `year`, "Sep 24 – 30, 2026". */
export const formatDayRange = (first: string, last: string, locale: string, { year = false } = {}): string => {
  const format = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', ...(year ? { year: 'numeric' } : {}), timeZone: 'UTC' })
  return first === last ? format.format(dayAsDate(first)) : format.formatRange(dayAsDate(first), dayAsDate(last))
}

/** A calendar day with its year, `YYYY-MM-DD`, as the reader writes dates: "31 Dec 2026". */
export const formatDay = (day: string, locale: string): string =>
  new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${day}T00:00:00.000Z`))

/** What stands in place of a missing figure: "Not recorded" for a period before records begin (S0 §21.5 rule 4). */
export const noFigureKey = (result: Pick<MetricResult, 'notes'>): string =>
  result.notes.some((note) => note.code === 'NOT_RECORDED') ? 'overview.snapshot.notRecorded' : 'overview.snapshot.notMeasurable'

/** Whether a change is for the better, the worse, or neither, for metrics that have a better way. */
export const verdictOf = (result: Pick<MetricResult, 'betterWhen' | 'change'>): 'BETTER' | 'WORSE' | null => {
  if (!result.betterWhen || !result.change || result.change.direction === 'STEADY') return null
  const lower = result.change.direction === 'DOWN'
  return lower === (result.betterWhen === 'LOWER') ? 'BETTER' : 'WORSE'
}
